import React, { useEffect, useState } from 'react';
import {
    Avatar,
    Badge,
    Box,
    Chip,
    IconButton,
    List,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    AddCommentOutlined,
    NavigateBefore,
    Search,
} from '@mui/icons-material';
import {
    ChannelList,
    ChannelPreviewUIComponentProps,
    ChannelSearch,
    useChatContext,
} from 'stream-chat-react';
import {
    ChannelFilters,
    ChannelOptions,
    ChannelSort,
    DefaultGenerics,
} from 'stream-chat';
import UserRole from '../../../../shared/AuthRoles';
import { useNavigate, useParams } from 'react-router-dom';
import SidebarMenu from './SidebarMenu';
import MessageComposer from './MessageComposer';

export const ChannelListItem = ({
    channel,
    active,
    displayImage,
    displayTitle,
    latestMessagePreview,
}: ChannelPreviewUIComponentProps<DefaultGenerics>) => {
    const { setActiveChannel } = useChatContext();
    const navigate = useNavigate();

    const handleSelect = () => {
        navigate(`/messages/${channel.id}`);

        setActiveChannel(channel);
    };

    const preview: string =
        typeof latestMessagePreview === 'string'
            ? latestMessagePreview
            : latestMessagePreview?.props.children;

    return (
        <ListItemButton disableRipple selected={active} onClick={handleSelect}>
            <ListItemAvatar>
                <Badge
                    overlap='circular'
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={channel.countUnread()}
                    color='primary'
                    sx={{
                        '& .MuiBadge-badge': {
                            border: '1px solid white',
                        },
                    }}
                >
                    <Avatar variant='rounded' src={displayImage} />
                </Badge>
            </ListItemAvatar>
            <ListItemText
                primaryTypographyProps={{
                    fontWeight: 'bold',
                }}
                secondaryTypographyProps={{
                    noWrap: true,
                }}
                primary={displayTitle}
                secondary={preview}
            />
        </ListItemButton>
    );
};

const CustomExitIcon = () => {
    return <NavigateBefore sx={{ color: 'text.primary' }} />;
};

export const CustomChannelList = ({
    onSelect,
    searchQuery,
    handleSearchChange,
    handleFilter,
    filterUnread,
    ...props
}: any) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
    const loggedInUser = UserRole();
    const [isSearching, setIsSearching] = useState<boolean>(false);

    const [composerOpen, setComposerOpen] = useState(false);

    const showToolbar =
        !loggedInUser?.role.includes('owner') || isMobile || isIpad;

    return (
        <>
            {showToolbar && <Toolbar />}
            <Paper
                sx={{
                    width: isMobile ? '100%' : 400,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'hidden',
                    borderRadius: 4,
                    my: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        pt: 3,
                        pb: 1,
                    }}
                >
                    <Typography variant='h3' component='h1'>
                        Messages
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {loggedInUser?.role.includes('owner') && (
                            <IconButton
                                sx={{ p: 0 }}
                                onClick={() => setComposerOpen(true)}
                            >
                                <AddCommentOutlined />
                            </IconButton>
                        )}
                        <SidebarMenu />
                    </Box>
                </Box>
                <ChannelSearch
                    searchForChannels
                    onSearch={() => setIsSearching(true)}
                    onSearchExit={() => setIsSearching(false)}
                    ExitSearchIcon={CustomExitIcon}
                    SearchInputIcon={Search}
                    searchQueryParams={{
                        channelFilters: {
                            filters: {
                                members: {
                                    $in: [UserRole().sub.toString()],
                                },
                            },
                        },
                        userFilters: {
                            filters: { username: '' },
                        },
                    }}
                />
                {!isSearching && (
                    <Stack direction='row' sx={{ gap: 0.5, px: 2, pb: 1 }}>
                        <Chip
                            variant={filterUnread ? 'outlined' : 'filled'}
                            label='All'
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleFilter(false)}
                        />
                        <Chip
                            variant={filterUnread ? 'filled' : 'outlined'}
                            label='Unread'
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleFilter(true)}
                        />
                    </Stack>
                )}
                {!isSearching && (
                    <List
                        onClick={onSelect}
                        sx={{ overflowY: 'scroll', maxHeight: '100%' }}
                        disablePadding
                    >
                        {props.children}
                    </List>
                )}
                <MessageComposer
                    open={composerOpen}
                    onClose={() => setComposerOpen(false)}
                />
            </Paper>
        </>
    );
};

interface SidebarProps {
    onSelect: () => void;
    open: boolean;
}

const MessagingSidebar = ({ onSelect, open }: SidebarProps) => {
    const loggedInUser = UserRole();
    const { id } = useParams();
    const { channel } = useChatContext();

    const navigate = useNavigate();

    const [filterUnread, setFilterUnread] = useState(false);

    const filters: ChannelFilters<DefaultGenerics> = {
        type: 'messaging',
        members: { $in: [loggedInUser.sub.toString()] },
        ...(filterUnread ? { has_unread: true } : undefined),
    };
    const options: ChannelOptions = { state: true, presence: true };
    const sort: ChannelSort = [{ last_message_at: -1 }];

    useEffect(() => {
        if (channel) {
            navigate(`/messages/${channel.id}`);
        }
    }, [channel]);

    return (
        <Box
            sx={{
                width: { xs: '100%', md: 400 },
                display: open ? 'block' : 'none',
            }}
        >
            <ChannelList
                customActiveChannel={id}
                filters={filters}
                options={options}
                sort={sort}
                // sendChannelsToList
                List={(props: any) => (
                    <CustomChannelList
                        onSelect={onSelect}
                        handleFilter={(v: boolean) => setFilterUnread(v)}
                        filterUnread={filterUnread}
                        {...props}
                    />
                )}
                Preview={(props) => <ChannelListItem {...props} />}
            />
        </Box>
    );
};

export default MessagingSidebar;
